import { createContext, useContext, useEffect, useReducer } from "react"
import { AuthenticationContext } from "../../AuthenticationContext"
import { useDebouncedState } from "../../hooks/useDebouncedState"
import { isValidEmail } from "../Users/usersModel"
import { useValidateUserEmailQuery } from "../Users/usersQueries"
import { TenantFormAction, tenantFormReducer } from "./tenantReducer"
import { areAllFieldsValid, checkIfTenantIsValid, emptyServerTenantImages, emptyTenantState, generateServerTenantImages, hasRolesChanged, hasTenantChanged, TenantFormState, TenantImageUrls } from "./tenantsModel"
import { useTenantsQuery } from "./tenantsQueries"

export type TenantsContextData = {
  state: TenantFormState
  dispatch: (action: TenantFormAction) => void
  isSaveButtonDisabled: boolean
  hasCustomerDisplayOptionChanged: boolean
  hasRolesChanged: boolean
  isTenantDataLoading: boolean
  isNewTenant: boolean
  isUSQuickBooksAccount: boolean
  isEmailAvailable: boolean
  isEmailValid: boolean
  isEmailValidationLoading: boolean
  savedPlanName: string
}

export const defaultTenantsContextValue = (): TenantsContextData => ({
  state: emptyTenantState(emptyServerTenantImages()),
  dispatch: () => { },
  isSaveButtonDisabled: false,
  hasCustomerDisplayOptionChanged: false,
  hasRolesChanged: false,
  isTenantDataLoading: false,
  isNewTenant: false,
  isUSQuickBooksAccount: false,
  isEmailAvailable: false,
  isEmailValid: false,
  isEmailValidationLoading: false,
  savedPlanName: '',
})

const TenantsContext = createContext<TenantsContextData>(defaultTenantsContextValue())

export default TenantsContext

export type Props = React.PropsWithChildren & {
  tenantId: number | undefined,
  imagesData?: TenantImageUrls,
  bucketName?: string,
  onTenantChange?: (hasTenantChanged: boolean) => void
}

export const TenantsContextWrapper = ({ tenantId, children, imagesData, bucketName, onTenantChange }: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const [state, dispatch] = useReducer(tenantFormReducer, emptyTenantState(imagesData ?? emptyServerTenantImages()))
  const { debouncedState: debouncedEmail, setState: setDebouncedEmail } = useDebouncedState(state.adminAccount?.email, 300)

  const tenantQuery = useTenantsQuery(tenantId)
  const validateUserEmailQuery = useValidateUserEmailQuery(debouncedEmail ?? '', true)

  const isNewTenant = tenantId === 0
  const isUSQuickBooksAccount = currentUser?.tenantQuickbooksCompanyCountryCode === 'US' || currentUser?.tenantQuickbooksCompanyCountryCode == null
  const hasCustomerDisplayOptionChanged = tenantQuery.data?.customerDisplayAsDefault !== state.tenant.customerDisplayAsDefault
  const isEmailAvailable = validateUserEmailQuery.data ?? false

  const isEmailValid = isValidEmail(state.adminAccount?.email ?? '')

  const hasTenantDataChanged = checkIfTenantIsValid(state.tenant) 
    && hasTenantChanged(tenantQuery.data!, { ...state, isAdminAccountEmailAvailable: isEmailAvailable }) 
  const areFormRequiredFieldsSet = areAllFieldsValid({ ...state, isAdminAccountEmailAvailable: isEmailAvailable }, isUSQuickBooksAccount)
  const isSaveButtonDisabled = isNewTenant 
    ? !areFormRequiredFieldsSet
    : !(hasTenantDataChanged && areAllFieldsValid)
  const hasRolesDataChanged = hasRolesChanged(tenantQuery.data!, state.tenant, state.roles)

  useEffect(() => {
    if (isNewTenant) {
      dispatch({ type: 'RESET_FORM', payload: emptyTenantState(emptyServerTenantImages()) })
      return
    }
    
    if (!tenantQuery.data) {
      return
    }

    const serverImages: TenantImageUrls = imagesData ?? generateServerTenantImages(bucketName, tenantQuery.data.path)

    dispatch({
      type: 'RESET_FORM', payload: {
        ...emptyTenantState(serverImages),
        tenant: tenantQuery.data,
      }
    })
  }, [tenantQuery.data, tenantId])

  useEffect(() => {
    setDebouncedEmail(state.adminAccount?.email)
  }, [state.adminAccount?.email])

  useEffect(() => {
    onTenantChange?.(hasTenantDataChanged)
  }, [hasTenantDataChanged, onTenantChange])

  return <TenantsContext.Provider
    value={{
      state,
      dispatch,
      isSaveButtonDisabled,
      hasCustomerDisplayOptionChanged,
      hasRolesChanged: hasRolesDataChanged,
      isTenantDataLoading: tenantQuery.isLoading,
      isNewTenant,
      isUSQuickBooksAccount,
      isEmailAvailable,
      isEmailValid,
      isEmailValidationLoading: validateUserEmailQuery.isLoading,
      savedPlanName: tenantQuery.data?.planName ?? '',
    }}
  >
    {children}
  </TenantsContext.Provider>
}